import React, { useCallback, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sendSignInLinkToEmail } from "firebase/auth";
import { auth } from "../../../../firebase/firebase";
import { useEffect, useState } from "react";
import { SignupContext } from "../../../../context/SignupContext";
import * as Yup from "yup";
import Button from "../../../../components/Button";
import Loader from "../../../../components/Loader";
import APILoader from "../../../../components/APILoader";
import { BASE_API_URL, BASE_URL } from "../../../../config";
import BirthdatePicker from "../../../../components/BirthdatePicker";
import { format } from "date-fns";

const Step1 = () => {
  const navigate = useNavigate();
  const [updatingUser, setUpdatingUser] = useState(false);
  let EMAIL_REGX =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

  const schema = Yup.object().shape({
    firstName: Yup.string().required(`First name is required!`),
    lastName: Yup.string().required(`Last name is required!`),
    email: Yup.string().required("Email is required").matches(EMAIL_REGX, "Email is not valid"),
  });

  const [params] = useSearchParams();

  const { formData, updateFormData } = useContext(SignupContext);
  const { firstName, lastName, email, dob } = formData;
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [isFormValid, setIsFormValid] = useState(false);

  const checkUserOrValidateDOB = async (email: string, dob: string) => {
    try {
      const response = await fetch(`${BASE_API_URL}/validateSignup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          dob,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors(prev => ({ ...prev, email: data.error }));
        return false;
      }

      console.log("Response:", data);
      return true;
    } catch (error) {
      console.error("Error:", error);
      return false;
    }
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      const validateDate = async (date: Date) => {
        try {
          await Yup.object()
            .shape({
              dob: Yup.date().required("Date is required").max(fiveYearsAgo, "Please make sure you use your real date of birth"),
            })
            .validateAt("dob", { dob: date });
          setErrors(prev => {
            const { dob: _, ...rest } = prev;
            return rest;
          });
          return true;
        } catch (err) {
          let error = err as Yup.ValidationError;
          setErrors(prev => ({ ...prev, dob: error.message }));
          return false;
        }
      };

      e.preventDefault();
      setUpdatingUser(true);
      const dobOk = await validateDate(dob);
      if (!dobOk) {
        setUpdatingUser(false);
        return;
      }

      const formatted = format(dob, "yyyy-MMM-dd");

      const isValid = await checkUserOrValidateDOB(email, formatted);
      if (!isValid) {
        setUpdatingUser(false);
        return;
      }
      try {
        const actionCodeSettings = {
          url: `${BASE_URL}/signup/verifyEmail?email=${email}&fname=${firstName}&lname=${lastName}`,
          handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        window.localStorage.setItem("emailForSignIn", email);
        window.localStorage.setItem("firstName", firstName);
        window.localStorage.setItem("lastName", lastName);

        navigate("./verify");
      } catch (err) {
        console.log(err);
      } finally {
        setUpdatingUser(false);
      }
    },
    [firstName, lastName, email, dob, navigate]
  );

  useEffect(() => {
    console.log("reloaded Register");
    let email = params.get("email");
    if (email) {
      const newValue = { ...formData, email };
      updateFormData(newValue);
    }
  }, [params]);

  const isValid = () => {
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    setErrors({ firstName: "", email: "", lastName: "" });
    return () => {};
  }, []);

  // const validateForm = async (name: string, value: string) => {
  //   try {
  //     await schema.validateAt(name, { [name]: value });

  //     setErrors(prev => {
  //       const { [name]: _, ...rest } = prev; // Destructure out the key to remove and capture the rest
  //       return rest;
  //     });
  //     return true;
  //   } catch (err) {
  //     let error = err as Yup.ValidationError;
  //     setErrors(prev => ({ ...prev, [name]: error.message }));
  //   }
  // };
  // const [emailId, setEmailId] = useState(context?.formData?.email ?? "");

  const updateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { name, value } = e.target;
    const newValue = { ...formData, [name]: value };
    updateFormData(newValue);
  };

  const handleDateChange = (date: Date) => {
    console.log("Selected Birthdate:", date.toDateString());
    const newValue = { ...formData, dob: date };
    updateFormData(newValue);
  };

  const onInputEvent = async (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (event.type === "focus") {
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return;
    }
    try {
      await schema.validateAt(name, { [name]: value });
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return true;
    } catch (err) {
      let error = err as Yup.ValidationError;
      setErrors(prev => ({ ...prev, [name]: error.message }));
    }
  };

  useEffect(() => {
    const validate = async (data: { [key: string]: string | Date | undefined }) => {
      try {
        await schema.validate(data, { abortEarly: true });
        setErrors({});
        setIsFormValid(true);
      } catch (err) {
        // let error = err as Yup.ValidationError;
        // const validationErrors: { [key: string]: string } = {};
        // if (error.inner && error.inner.length > 0) {
        //   error.inner.forEach(err => {
        //     if (err.path) {
        //       validationErrors[err.path] = err.message;
        //     }
        //   });
        // } else if (error.path) {
        //   // Catch the first error when abortEarly is true
        //   validationErrors[error.path] = error.message;
        // }
        // setErrors(validationErrors);
        setIsFormValid(false);
      }
    };
    const { firstName, lastName, email } = formData;
    validate({ firstName, lastName, email });
  }, [formData]);

  return (
    <>
      {/* {updatingUser && <Loader />} */}
      <APILoader loading={updatingUser} />
      <section>
        <div className="container">
          <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2 gap-x-5 gap-y-5">
            {/* Left Container */}
            <div className="flex flex-col items-start justify-start">
              <h1 className="herotitle">Skarvo</h1>
              <p className="text-xl font-normal">Create your Skarvo Business account.</p>
            </div>

            {/* Right Container */}
            <div className="flex flex-col gap-10">
              <div className="flex flex-col items-start formcard">
                <div className="flex flex-row items-start formheader">
                  <div className="text-xl font-bold text-center mt-[15px] mb-[30px]">Sign Up</div>
                </div>
                <div className="flex justify-center w-full px-[30px] mb-[30px]">
                  <div className=" bg-skseparatorgray w-full rounded-full h-[5px]">
                    <div className=" bg-skgreen h-[5px] rounded-full" style={{ width: "25%" }}></div>
                  </div>
                </div>

                <div className="mb-5 px-[30px] flex flex-col w-full">
                  <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="First Name"
                    required
                    onChange={updateValue}
                    onBlur={onInputEvent}
                    onFocus={onInputEvent}
                    value={firstName}
                  />
                  <span className="block h-5 mb-2 text-sm text-red-600 dark:text-red-500">{errors.firstName ?? ""}</span>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Last Name"
                    required
                    onBlur={onInputEvent}
                    onChange={updateValue}
                    onFocus={onInputEvent}
                    value={lastName}
                  />
                  {errors.lastName && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.lastName}</span>}
                  {!errors.lastName && (
                    <span className="block mb-2 text-sm text-gray-400 dark:text-gray-600">Your name as shown on a government issued ID.</span>
                  )}
                </div>

                <BirthdatePicker onChange={date => handleDateChange(date)} error={errors.dob} />

                <div className="mb-5 px-[30px] flex flex-col space-x-[10x] w-full">
                  <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="xyz@yourdomain.com"
                    required
                    onChange={updateValue}
                    onBlur={onInputEvent}
                    onFocus={onInputEvent}
                    value={formData.email}
                  />
                  {errors.email && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.email}</span>}
                  {!errors.email && <span className="block mb-2 text-sm text-gray-400 dark:text-gray-600">Your email will be your account ID.</span>}
                </div>
                <div className="thickseparator"></div>
                <div className="flex flex-col items-center justify-between w-full px-[30px] py-[20px] md:flex-row-reverse  gap-y-[30px]">
                  <Button to="#" onClick={handleSubmit} className="w-full md:w-auto md:min-w-[120px] formprimarybutton" isDisabled={!isFormValid}>
                    Continue
                  </Button>
                  <Link to="/" className="w-full md:w-auto md:min-w-[120px] formsecondarybutton">
                    Cancel
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Step1;
